import { normalizeCrmActionHref } from "./safe-action-hrefs";
import { buildCrmContactChannelActions } from "./contact-action-groups";

export type CrmTaskActionTone = "neutral" | "primary" | "success" | "warning" | "danger";

export interface CrmTaskAction {
  key: string;
  label: string;
  description: string | null;
  href: string;
  tone: CrmTaskActionTone;
  disabled: boolean;
  disabledReason: string | null;
}

export interface CrmTaskActionGroup {
  key: string;
  label: string;
  description: string | null;
  source: "configured";
  actions: CrmTaskAction[];
}

export interface BuildCrmTaskActionGroupsOptions {
  actionGroupKeys?: readonly string[];
  maxGroups?: number;
  maxActionsPerGroup?: number;
}

export interface BuildCrmTaskContactChannelActionsOptions {
  channelKeys?: readonly string[];
  maxActions?: number;
}

export const CRM_TASK_ACTION_GROUP_FIELD_KEYS = [
  "crmTaskActionGroups",
  "taskActionGroups",
  "taskActions",
  "operatorActionGroups",
] as const;

export const CRM_TASK_CONTACT_CHANNEL_FIELD_KEYS = [
  "crmTaskContactChannels",
  "taskContactChannels",
  "taskChannels",
  "contactChannels",
  "communicationChannels",
] as const;

const PRIVATE_KEY_PATTERN = /(?:password|secret|token|authorization|credential|private[_-]?key|api[_-]?key)/i;

export function buildCrmTaskActionGroups(
  taskRecords: unknown,
  options: BuildCrmTaskActionGroupsOptions = {},
): CrmTaskActionGroup[] {
  const records = toRecords(taskRecords);
  if (records.length === 0) return [];

  const actionGroupKeys = options.actionGroupKeys?.length
    ? [...options.actionGroupKeys]
    : [...CRM_TASK_ACTION_GROUP_FIELD_KEYS];
  const maxGroups = normalizePositiveInteger(options.maxGroups, 6);
  const maxActionsPerGroup = normalizePositiveInteger(options.maxActionsPerGroup, 8);

  return records
    .flatMap((record) => actionGroupKeys.flatMap((key) => parseConfiguredActionGroups(key, record[key], maxActionsPerGroup)))
    .filter((group) => group.actions.length > 0)
    .slice(0, maxGroups);
}

export function buildCrmTaskContactChannelActions(
  taskRecords: unknown,
  options: BuildCrmTaskContactChannelActionsOptions = {},
): CrmTaskAction[] {
  const records = toRecords(taskRecords);
  if (records.length === 0) return [];

  const channelKeys = options.channelKeys?.length
    ? options.channelKeys
    : CRM_TASK_CONTACT_CHANNEL_FIELD_KEYS;
  const maxActions = normalizePositiveInteger(options.maxActions, 8);
  const actions = records.flatMap((record) => (
    buildCrmContactChannelActions(record, { channelKeys, maxActions })
  ));
  return uniqueActions(actions).slice(0, maxActions);
}

export function buildCrmTaskContactChannelActionGroup(
  taskRecords: unknown,
  options: BuildCrmTaskContactChannelActionsOptions = {},
): CrmTaskActionGroup | null {
  const actions = buildCrmTaskContactChannelActions(taskRecords, options);
  if (actions.length === 0) return null;

  return {
    key: "taskContactChannels",
    label: "Contact channels",
    description: "Safe contact-channel actions supplied by task metadata or context.",
    source: "configured",
    actions,
  };
}

function parseConfiguredActionGroups(
  rootKey: string,
  value: unknown,
  maxActionsPerGroup: number,
): CrmTaskActionGroup[] {
  if (Array.isArray(value)) {
    return value
      .map((entry, index) => parseActionGroup(`${rootKey}.${index}`, entry, maxActionsPerGroup))
      .filter((group): group is CrmTaskActionGroup => Boolean(group));
  }

  const record = asRecord(value);
  if (!record) return [];

  if (hasGroupShape(record)) {
    const group = parseActionGroup(rootKey, record, maxActionsPerGroup);
    return group ? [group] : [];
  }

  return Object.entries(record)
    .map(([key, entry]) => parseActionGroup(key, entry, maxActionsPerGroup))
    .filter((group): group is CrmTaskActionGroup => Boolean(group));
}

function parseActionGroup(
  fallbackKey: string,
  value: unknown,
  maxActionsPerGroup: number,
): CrmTaskActionGroup | null {
  const record = asRecord(value);
  if (!record) return null;

  const key = readString(record.key) ?? fallbackKey;
  if (isPrivateKey(key)) return null;

  const label = readString(record.label)
    ?? readString(record.title)
    ?? formatLabel(key);
  const description = readString(record.description) ?? readString(record.summary);
  const rawActions = record.actions ?? record.links ?? record.buttons ?? record.items ?? null;
  const actions = parseActions(rawActions ?? record, maxActionsPerGroup);

  return {
    key,
    label,
    description,
    source: "configured",
    actions,
  };
}

function parseActions(value: unknown, maxActions: number): CrmTaskAction[] {
  if (Array.isArray(value)) {
    return value
      .map((entry, index) => parseAction(`action-${index + 1}`, entry))
      .filter((action): action is CrmTaskAction => Boolean(action))
      .slice(0, maxActions);
  }

  const record = asRecord(value);
  if (!record) return [];

  const ignoredGroupKeys = new Set(["key", "label", "title", "description", "summary", "actions", "links", "buttons", "items"]);
  return Object.entries(record)
    .filter(([key]) => !ignoredGroupKeys.has(key))
    .map(([key, entry]) => parseAction(key, entry))
    .filter((action): action is CrmTaskAction => Boolean(action))
    .slice(0, maxActions);
}

function parseAction(fallbackKey: string, value: unknown): CrmTaskAction | null {
  if (isPrivateKey(fallbackKey)) return null;

  const record = asRecord(value);
  if (!record) return null;

  const key = readString(record.key) ?? fallbackKey;
  if (isPrivateKey(key)) return null;

  const href = normalizeCrmActionHref(readString(record.href) ?? readString(record.url) ?? readString(record.to));
  if (!href) return null;

  const label = readString(record.label)
    ?? readString(record.title)
    ?? readString(record.actionLabel)
    ?? formatLabel(key);
  const description = readString(record.description) ?? readString(record.helpText);
  const disabled = record.disabled === true;
  const disabledReason = disabled
    ? readString(record.disabledReason) ?? readString(record.reason)
    : null;

  return {
    key,
    label,
    description,
    href,
    tone: normalizeTone(readString(record.tone) ?? readString(record.status)),
    disabled,
    disabledReason,
  };
}

function toRecords(value: unknown): Record<string, unknown>[] {
  if (Array.isArray(value)) {
    return value
      .map(asRecord)
      .filter((record): record is Record<string, unknown> => Boolean(record));
  }
  const record = asRecord(value);
  return record ? [record] : [];
}

function hasGroupShape(record: Record<string, unknown>): boolean {
  return ["label", "title", "description", "summary", "actions", "links", "buttons", "items"].some((key) => key in record);
}

function normalizeTone(value: string | null): CrmTaskActionTone {
  if (value === "primary" || value === "success" || value === "warning" || value === "danger") return value;
  if (value === "critical" || value === "error" || value === "failed" || value === "blocked") return "danger";
  if (value === "ready" || value === "passed" || value === "complete" || value === "recommended") return "success";
  if (value === "review" || value === "hold" || value === "partial") return "warning";
  return "neutral";
}

function uniqueActions(actions: CrmTaskAction[]): CrmTaskAction[] {
  const seen = new Set<string>();
  return actions.filter((action) => {
    if (seen.has(action.href)) return false;
    seen.add(action.href);
    return true;
  });
}

function normalizePositiveInteger(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : fallback;
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function isPrivateKey(key: string): boolean {
  return PRIVATE_KEY_PATTERN.test(key);
}

function formatLabel(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
